import { apiUrl, sleep } from "../../dataProvider";
import axios from "axios";

/**
 * Processes records for CSV export, chunking data and fetching additional information as needed
 * based on the specified resource type. A callback function is called to track loading progress.
 *
 * @param body - A form data object.
 * @param records - An array of records to process.
 * @param callback - A function to handle the loading state and progress updates.
 * @returns An array containing processed data ready for CSV export.
 */
export const processParentRecordsForCsv = async (
  body: any,
  records: any[],
  callback: (progressUpdate: {
    isLoading?: boolean;
    loadedRecordCount: number;
    totalRecords: number;
  }) => void
) => {
  // Split the records array into chunks of the specified size
  const chunkSize = 100;
  const chunks = Array.from(
    { length: Math.ceil(records.length / chunkSize) },
    (_, i) => records.slice(i * chunkSize, i * chunkSize + chunkSize)
  );

  let processedData: any[] = [];

  // Loop over each chunk to process records in manageable batches
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    // Process all records within the chunk concurrently
    const chunkResults = await Promise.all(
      chunk.map(async (record, recordIndex) => {
        // Delay each request by 100ms * recordIndex within the chunk
        await sleep(recordIndex * 100);

        // Calculate the current record index for progress tracking and
        // update the progress using the callback
        const currentIndex = chunkIndex * chunkSize + recordIndex + 1;
        callback({
          loadedRecordCount: currentIndex,
          totalRecords: records.length,
        });

        try {
          const customerCards = await axios.get(`${apiUrl}/customerCards`, {
            params: {
              filter: JSON.stringify({
                CustomerNo: record.user.mbcCustomerId,
              }),
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          return {
            no: currentIndex,
            id: record.id,
            name: `${record.firstName} ${record.lastName}`,
            email: record.user.email,
            mobile: record.mobile,
            schoolCode: body.school.mbcCode,
            mbcCustomerId: record.user.mbcCustomerId,
            outstandingAmount:
              customerCards.data.values[0]?.balanceLCY ?? "record not found",
          };
        } catch {
          throw new Error("Failed to fetch parent record");
        }
      })
    );

    // Append processed chunk results to the main processed data array
    processedData = [...processedData, ...chunkResults.flat()];
  }

  // Return the fully processed data for CSV export
  return processedData;
};
